import { Box, List, Stack } from '@mui/material';
import ListSubheader, { ListSubheaderProps } from '@mui/material/ListSubheader';
import { styled } from '@mui/material/styles';
import { PropsWithChildren, useMemo } from 'react';
import { MemoizedGlobalSearch } from '@/components/global-search/GlobalSearch';
import Logo from '@/components/Logo';
import LogoSmall from '@/components/LogoSmall';
import { NavListRoot } from '@/components/nav-section/NavList';
import { useSession } from '@/components/session/useSession';
import useCollapseDrawer from '@/hooks/useCollapseDrawer';
import useResponsive from '@/hooks/useResponsive';
import { CollapseButton } from '@/layouts/dashboard/navbar/CollapseButton';
import { NavbarOrg } from '@/layouts/dashboard/navbar/NavbarOrg';
import getNavConfig from '@/layouts/dashboard/navbar/NavConfig';

const ListSubheaderStyle = styled((props: PropsWithChildren<ListSubheaderProps>) => (
  <ListSubheader disableSticky disableGutters {...props} />
))(({ theme }) => ({
  ...theme.typography.overline,
  paddingLeft: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  color: theme.palette.text.primary,
  transition: theme.transitions.create('opacity', {
    duration: theme.transitions.duration.shorter,
  }),
}));

type Props = {
  onCloseSidebar: VoidFunction;
};

export const NavBarItems = ({ onCloseSidebar }: Props) => {
  const isDesktop = useResponsive('up', 'lg');
  const { isCollapse, collapseHover, onToggleCollapse } = useCollapseDrawer();

  const {
    org: { orgSector, isEconomicUnitsToggled },
    featureFlags: {
      REPORTING_MENU_OPTION_ENABLED,
      CBI_ENABLED,
      CONSUMPTION_HUB,
      ENERGY_NETWORKS,
      APPRAISAL_REVIEW_ENABLED,
    },
    isAdmin,
  } = useSession();

  const navConfig = useMemo(
    () =>
      getNavConfig({
        isEconomicUnitsToggled,
        reportingToggled: REPORTING_MENU_OPTION_ENABLED,
        cbiEnabled: CBI_ENABLED,
        consumptionHubEnabled: CONSUMPTION_HUB,
        energyNetworksEnabled: ENERGY_NETWORKS,
        appraisalReviewEnabled: APPRAISAL_REVIEW_ENABLED,
        orgSector,
        isAdmin,
      }),
    [
      isEconomicUnitsToggled,
      REPORTING_MENU_OPTION_ENABLED,
      CBI_ENABLED,
      CONSUMPTION_HUB,
      ENERGY_NETWORKS,
      APPRAISAL_REVIEW_ENABLED,
      orgSector,
      isAdmin,
    ],
  );

  return (
    <Stack direction="column" justifyContent="space-between" sx={{ height: '100%', overflow: 'hidden' }}>
      <Stack>
        <Stack
          spacing={3}
          sx={{
            py: 2,
            flexShrink: 0,
            pl: 3.5,
            pr: 2,
            height: 72,
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            {!isCollapse ? <Logo sx={{ height: 28 }} /> : <LogoSmall sx={{ width: 21, pt: 1 }} />}

            {isDesktop && !isCollapse ? (
              <CollapseButton onToggleCollapse={onToggleCollapse} collapseHover={collapseHover} />
            ) : null}
          </Stack>
        </Stack>

        <Box>
          <MemoizedGlobalSearch isCollapse={isCollapse} />
          {navConfig.map((group) => (
            <List key={group.subheader} disablePadding sx={{ px: 0.5 }}>
              {group.subheader !== '' ? (
                <ListSubheaderStyle
                  sx={{
                    ...(isCollapse && {
                      opacity: 0,
                    }),
                  }}
                >
                  {group.subheader}
                </ListSubheaderStyle>
              ) : null}

              {group.items.map((list) => (
                <NavListRoot
                  key={list.rootPathname}
                  list={list}
                  isCollapse={isCollapse}
                  onNavItemClick={onCloseSidebar}
                />
              ))}
            </List>
          ))}
        </Box>
      </Stack>
      <Stack sx={{ px: 0.5 }}>
        <NavbarOrg isCollapse={isCollapse} onNavItemClick={onCloseSidebar} />
      </Stack>
    </Stack>
  );
};
